<template>
  <div v-if="model">
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="450"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          class="pl-1"
          extension-height="40"
          flat
          height="80"
          max-height="120"
        >
          <v-avatar :color="$t('Settings.VoipDomain.Dialog.VoipUser.Toolbar.Color')">
            <v-icon dark>
              {{ $t('Settings.VoipDomain.Dialog.VoipUser.Toolbar.Icon') }}
            </v-icon>
          </v-avatar>

          <v-toolbar-title class="pl-3">
            <div class="body-3">
              {{ isEditMode ? $t('Settings.VoipDomain.Dialog.VoipUser.Toolbar.Edit.Title') : $t('Settings.VoipDomain.Dialog.VoipUser.Toolbar.Add.Title') }}
            </div>

            <div class="text-caption">
              {{ isEditMode ? $t('Settings.VoipDomain.Dialog.VoipUser.Toolbar.Edit.Subtitle') : $t('Settings.VoipDomain.Dialog.VoipUser.Toolbar.Add.Subtitle') }}:

              {{ voipDomain.Domain.DomainLive }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text class="pa-3">
          <v-form
            ref="dataForm"
            v-model="dataFormValid"
            @submit.prevent
          >
            <v-container
              class="pa-0"
              fluid
            >
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    :label="$t('Settings.VoipDomain.Dialog.VoipUser.Filed.Domain.Label')"
                    :value="voipDomain.Domain.DomainLive"
                    disabled
                  />
                </v-col>

                <v-col
                  cols="12"
                >
                  <v-text-field
                    v-if="isEditMode"
                    :label="$t('Settings.VoipDomain.Dialog.VoipUser.Filed.UserId.Label')"
                    :value="user.Uid"
                    disabled
                  />
                  <v-select
                    v-else
                    v-model="model.UserId"
                    :disabled="isEditMode"
                    :items="domainUsers"
                    :label="$t('Settings.VoipDomain.Dialog.VoipUser.Filed.UserId.Label')"
                    :rules="validationRules.dataForm.UserId"
                    clearable
                    item-text="Uid"
                    item-value="Id"
                  />
                </v-col>

                <v-col cols="6">
                  <v-text-field
                    v-model="model.VoipAgent"
                    v-mask="'######'"
                    :label="$t('Settings.VoipDomain.Dialog.VoipUser.Filed.VoipAgent.Label')"
                    :rules="validationRules.dataForm.VoipAgent"
                    clearable
                    required
                  />
                </v-col>

                <v-col cols="6">
                  <v-text-field
                    v-model="model.Queue"
                    v-mask="'######'"
                    :label="$t('Settings.VoipDomain.Dialog.VoipUser.Filed.Queue.Label')"
                    :rules="validationRules.dataForm.Queue"
                    clearable
                    required
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />

          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) }}
          </v-btn>

          <v-btn
            :disabled="isSaving"
            :loading="isSaving"
            class="green white--text elevation-0"
            @click="onSaveButtonClick"
          >
            {{ $t('Common.Button.Save').toLocaleUpperCase($i18n.locale) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import {clone} from '@/lib/helper/helper'

export default {
  name      : 'VoipUserEditDialog',
  components: {},
  mixins    : [AppData, Auth],
  props     : {
    visible: {
      type   : Boolean,
      default: false
    },
    user: {
      type   : Object,
      default: undefined
    },
    voipDomain: {
      type   : Object,
      default: undefined
    }
  },
  data() {
    return {
      dataFormValid  : false,
      isSaving       : false,
      model          : null,
      validationRules: {
        dataForm: {
          UserId: [
            (v) => !!v || this.$t('Settings.VoipDomain.Dialog.VoipUser.Filed.UserId.Error.Required')
          ],
          VoipId: [
            (v) => !!v || this.$t('Settings.VoipDomain.Dialog.VoipUser.Filed.VoipId.Error.Required')
          ],
          VoipAgent: [
            (v) => !!v || this.$t('Settings.VoipDomain.Dialog.VoipUser.Filed.VoipAgent.Error.Required')
          ],
          Queue: [
            (v) => !!v || this.$t('Settings.VoipDomain.Dialog.VoipUser.Filed.Queue.Error.Required')
          ]
        }
      }
    }
  },
  computed: {
    domainUsers() {
      return this.voipDomain?.Domain?.Users?.filter(user => !user.IsWaiter) || []
    },

    isEditMode() {
      return !!this.model?.Id
    },

    isVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        if (this.$refs.dataForm) this.$refs.dataForm.resetValidation()

        if (this.user) {
          this.model = {
            Id       : this.user?.Id || null,
            UserId   : this.user?.UserId || null,
            VoipId   : this.user?.VoipId || null,
            VoipAgent: this.user?.VoipAgent || '',
            Queue    : this.user?.Queue || ''
          }
        } else {
          this.model = {
            Id       : null,
            UserId   : null,
            VoipId   : this.voipDomain?.VoipId || null,
            VoipAgent: this.user?.VoipAgent || '',
            Queue    : this.user?.Queue || ''
          }
        }

        this.$bus.$on(window.SocketCommand.Settings.VoipDomains.User.Save, this.onUserSaveResult)
      } else {
        this.$bus.$off(window.SocketCommand.Settings.VoipDomains.User.Save, this.onUserSaveResult)
      }

      this.isSaving = false
    }
  },
  created() {
  },
  mounted() {
  },
  updated() {
  },
  beforeDestroy() {
  },
  methods: {
    onSaveButtonClick() {
      this.$refs.dataForm.validate()

      if (this.dataFormValid) {
        this.isSaving = true
        const payload = clone(this.model)
        window.callAS(window.SocketCommand.Settings.VoipDomains.User.Save, payload)
      }
    },

    onUserSaveResult(data, payload) {
      if (data?.status === 'success') {
        this.$emit('save-success', payload)
        this.onCancelButtonClick()
      }

      this.isSaving = false
    },

    onCancelButtonClick() {
      if (this.$refs.dataForm) this.$refs.dataForm.resetValidation()
      this.isVisible = false
      this.isSaving = false
      this.model = null
    }
  }
}
</script>
